export interface IDBStoreConfig {
  tableName: string
  keyPath?: string
  autoIncrement?: boolean
  indices?: IDBIndexConfig[]
}

export interface IDBOptions {
  dbName: string
  version?: number
  stores?: IDBStoreConfig[]
}

export interface IDBObjectStoreOptions {
  keyPath?: string
  autoIncrement?: boolean
  indices?: IDBIndexConfig[]
}

export interface IDBIndexConfig {
  fieldName: string
  keyPath?: string | string[]
  options?: IDBIndexParameters
}

export interface IDBQueryOptions {
  indexName?: string
  range?: IDBKeyRange
  direction?: IDBCursorDirection
  limit?: number
}

let currentDB: IDBDatabase | null = null
let currentDBName: string = ''

/**
 * @doc true
 * @description 打开或创建 IndexedDB 数据库
 * @param {IDBOptions} options - 数据库配置选项
 * @param {string} options.dbName - 数据库名称
 * @param {number} [options.version=1] - 数据库版本
 * @param {IDBStoreConfig[]} [options.stores] - 要创建的表配置
 * @returns {Promise<IDBDatabase>} IndexedDB 数据库实例
 * @example
 * // 基本打开数据库
 * const db = await PRL.IDBOpen({ dbName: 'myApp', version: 1 })
 * 
 * // 创建表和索引
 * const db = await PRL.IDBOpen({ 
 *   dbName: 'myApp', 
 *   version: 1, 
 *   stores: [{
 *     tableName: 'users',
 *     keyPath: 'id',
 *     autoIncrement: true,
 *     indices: [{ fieldName: 'name' }, { fieldName: 'age' }]
 *   }]
 * })
 */
export function IDBOpen(options: IDBOptions): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const { dbName, version = 1, stores = [] } = options
    
    if (currentDB && currentDBName === dbName) {
      resolve(currentDB)
      return
    }
    
    // 解决版本冲突：如果请求版本低于现有版本，删除数据库后重建
    const tryOpen = () => {
      const request = indexedDB.open(dbName, version)
      
      request.onerror = () => {
        const errorMsg = request.error?.message || ''
        // 检测版本冲突错误：Requested version (X) is less than existing version (Y)
        if (errorMsg.includes('version') || (request.error as any)?.name === 'VersionError') {
          // 版本冲突，删除数据库后重试
          const deleteReq = indexedDB.deleteDatabase(dbName)
          deleteReq.onsuccess = () => {
            // 删除成功后重试打开
            const retryReq = indexedDB.open(dbName, version)
            retryReq.onerror = () => reject(new Error(`Failed to open database after cleanup: ${retryReq.error?.message}`))
            retryReq.onsuccess = () => {
              currentDB = retryReq.result
              currentDBName = dbName
              resolve(currentDB)
            }
            retryReq.onupgradeneeded = (event) => {
              const upgradeRequest = event.target as IDBOpenDBRequest
              createStores(upgradeRequest.result, upgradeRequest.transaction)
            }
          }
          deleteReq.onerror = () => reject(new Error(`Failed to delete outdated database: ${deleteReq.error?.message}`))
        } else {
          reject(new Error(`Failed to open database: ${errorMsg}`))
        }
      }
      
      request.onsuccess = () => {
        currentDB = request.result
        currentDBName = dbName
        resolve(currentDB)
      }
      
      request.onupgradeneeded = (event) => {
        const upgradeRequest = event.target as IDBOpenDBRequest
        createStores(upgradeRequest.result, upgradeRequest.transaction)
      }
    }
    
    // 创建表和索引的辅助函数
    const createStores = (db: IDBDatabase, transaction: IDBTransaction | null) => {
      currentDB = db
      currentDBName = dbName
      if (!transaction) {
        throw new Error('Missing version upgrade transaction while creating stores.')
      }
      
      stores.forEach((storeConfig) => {
        const { tableName, keyPath = 'id', autoIncrement = true, indices = [] } = storeConfig
        let store: IDBObjectStore
        
        if (!db.objectStoreNames.contains(tableName)) {
          store = db.createObjectStore(tableName, { keyPath, autoIncrement })
        } else {
          store = transaction.objectStore(tableName)
        }
        
        indices.forEach((index) => {
          const indexKeyPath = index.keyPath || index.fieldName
          if (!store.indexNames.contains(index.fieldName)) {
            store.createIndex(index.fieldName, indexKeyPath, index.options || {})
          }
        })
      })
    }
    
    tryOpen()
  })
}
/**
 * @doc true
 * @description 向指定的表中添加一条数据
 * @param {string} storeName - 表名
 * @param {T} item - 要添加的数据对象
 * @returns {Promise<IDBValidKey>} 新添加数据的主键
 * @example
 * // 添加一条数据
 * const id = await PRL.IDBAdd('users', { name: '张三', age: 25 })
 * 
 * // 批量添加
 * await PRL.IDBAdd('users', { name: '李四', age: 30 })
 * await PRL.IDBAdd('users', { name: '王五', age: 20 })
 */
export function IDBAdd<T>(storeName: string, item: T): Promise<IDBValidKey> {
  return new Promise((resolve, reject) => {
    if (!currentDB) {
      reject(new Error('Database not opened. Call IDBOpen first.'))
      return
    }
    
    const transaction = currentDB.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.add(item)
    
    request.onsuccess = () => {
      resolve(request.result)
    }
    
    request.onerror = () => {
      reject(new Error(`Failed to add item: ${request.error?.message}`))
    }
  })
}
/**
 * @doc true
 * @description 按条件删除表中的数据
 * @param {string} storeName - 表名
 * @param {(item: T) => boolean} queryFn - 条件判断函数，返回 true 则删除该数据
 * @returns {Promise<number>} 删除的数据数量
 * @example
 * // 删除 name 是"张三"的所有用户
 * const count = await PRL.IDBDeleteByQuery('users', (user) => user.name === '张三')
 * 
 * // 删除年龄大于 60 的用户
 * await PRL.IDBDeleteByQuery('users', (user) => user.age > 60)
 */
export function IDBDeleteByQuery<T>(
  storeName: string,
  queryFn: (item: T) => boolean
): Promise<number> {
  return new Promise(async (resolve, reject) => {
    if (!currentDB) {
      reject(new Error('Database not opened. Call IDBOpen first.'))
      return
    }
    
    try {
      const itemsToDelete = await IDBQuery<T>(storeName, queryFn)
      const deletePromises = itemsToDelete.map((item: any) => {
        const key = item[currentDB!.transaction(storeName).objectStore(storeName).keyPath as string]
        return IDBDelete(storeName, key)
      })
      await Promise.all(deletePromises)
      resolve(itemsToDelete.length)
    } catch (error: any) {
      reject(new Error(`Failed to delete by query: ${error.message}`))
    }
  })
}
/**
 * @doc true
 * @description 按条件更新表中的数据
 * @param {string} storeName - 表名
 * @param {(item: T) => boolean} queryFn - 条件判断函数，返回 true 则更新该数据
 * @param {(item: T) => T} updateFn - 更新函数，返回更新后的数据
 * @returns {Promise<number>} 更新的数据数量
 * @example
 * // 将 name 是"李四"的用户状态改为"active"
 * const count = await PRL.IDBUpdateByQuery(
 *   'users',
 *   (user) => user.name === '李四',
 *   (user) => ({ ...user, status: 'active' })
 * )
 * 
 * // 将所有年龄大于 18 的用户标记为成年人
 * await PRL.IDBUpdateByQuery(
 *   'users',
 *   (user) => user.age >= 18,
 *   (user) => ({ ...user, isAdult: true })
 * )
 */
export function IDBUpdateByQuery<T>(
  storeName: string,
  queryFn: (item: T) => boolean,
  updateFn: (item: T) => T
): Promise<number> {
  return new Promise(async (resolve, reject) => {
    if (!currentDB) {
      reject(new Error('Database not opened. Call IDBOpen first.'))
      return
    }
    
    try {
      const itemsToUpdate = await IDBQuery<T>(storeName, queryFn)
      const updatePromises = itemsToUpdate.map((item) => {
        const updatedItem = updateFn(item)
        return IDBUpdate(storeName, updatedItem)
      })
      await Promise.all(updatePromises)
      resolve(itemsToUpdate.length)
    } catch (error: any) {
      reject(new Error(`Failed to update by query: ${error.message}`))
    }
  })
}
/**
 * @doc true
 * @description 按条件查询表中的数据
 * @param {string} storeName - 表名
 * @param {(item: T) => boolean} queryFn - 条件判断函数，返回 true 则包含该数据
 * @param {IDBQueryOptions} [options] - 查询选项
 * @param {string} [options.indexName] - 索引名称
 * @param {IDBKeyRange} [options.range] - 键范围
 * @param {IDBCursorDirection} [options.direction] - 游标方向
 * @param {number} [options.limit] - 限制返回数量
 * @returns {Promise<T[]>} 符合条件的数据数组
 * @example
 * // 查询 name 是"张三"的所有用户
 * const users = await PRL.IDBQuery('users', (user) => user.name === '张三')
 * 
 * // 查询年龄大于等于 20 的用户
 * const adults = await PRL.IDBQuery('users', (user) => user.age >= 20)
 * 
 * // 限制返回数量
 * const top3 = await PRL.IDBQuery('users', () => true, { limit: 3 })
 */
export function IDBQuery<T>(
  storeName: string,
  queryFn: (item: T) => boolean,
  options?: IDBQueryOptions
): Promise<T[]> {
  return new Promise((resolve, reject) => {
    if (!currentDB) {
      reject(new Error('Database not opened. Call IDBOpen first.'))
      return
    }
    
    const transaction = currentDB.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    
    const results: T[] = []
    let request: IDBRequest<IDBCursorWithValue | null>
    
    if (options?.indexName) {
      const index = store.index(options.indexName)
      request = index.openCursor(options.range, options.direction)
    } else {
      request = store.openCursor(options?.range, options?.direction)
    }
    
    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result
      
      if (cursor) {
        if (queryFn(cursor.value)) {
          results.push(cursor.value)
        }
        
        if (options?.limit && results.length >= options.limit) {
          resolve(results)
          return
        }
        
        cursor.continue()
      } else {
        resolve(results)
      }
    }
    
    request.onerror = () => {
      reject(new Error(`Failed to query items: ${request.error?.message}`))
    }
  })
}
/**
 * @doc true
 * @description 统计表中数据的数量
 * @param {string} storeName - 表名
 * @param {string} [indexName] - 索引名称（可选）
 * @param {IDBKeyRange} [range] - 键范围（可选）
 * @returns {Promise<number>} 数据数量
 * @example
 * // 统计 users 表的总记录数
 * const total = await PRL.IDBCount('users')
 * 
 * // 使用索引统计
 * const zhangCount = await PRL.IDBCount('users', 'name', IDBKeyRange.only('张三'))
 * 
 * // 统计年龄大于等于 20 的用户
 * const adultCount = await PRL.IDBCount('users', 'age', IDBKeyRange.lowerBound(20))
 */
export function IDBCount(storeName: string, indexName?: string, range?: IDBKeyRange): Promise<number> {
  return new Promise((resolve, reject) => {
    if (!currentDB) {
      reject(new Error('Database not opened. Call IDBOpen first.'))
      return
    }
    
    const transaction = currentDB.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    
    let request: IDBRequest<number>
    
    if (indexName) {
      const index = store.index(indexName)
      request = index.count(range)
    } else {
      request = store.count(range)
    }
    
    request.onsuccess = () => {
      resolve(request.result)
    }
    
    request.onerror = () => {
      reject(new Error(`Failed to count items: ${request.error?.message}`))
    }
  })
}
/**
 * @doc true
 * @description 获取表中所有数据，支持索引和范围查询
 * @param {string} storeName - 表名
 * @param {IDBQueryOptions} [options] - 查询选项
 * @param {string} [options.indexName] - 索引名称
 * @param {IDBKeyRange} [options.range] - 键范围
 * @param {IDBCursorDirection} [options.direction] - 游标方向
 * @param {number} [options.limit] - 限制返回数量
 * @returns {Promise<T[]>} 数据数组
 * @example
 * // 获取所有数据
 * const allUsers = await PRL.IDBGetAll('users')
 * 
 * // 使用索引查询
 * const users = await PRL.IDBGetAll('users', { 
 *   indexName: 'name', 
 *   range: IDBKeyRange.only('张三') 
 * })
 * 
 * // 限制返回数量
 * const top10 = await PRL.IDBGetAll('users', { limit: 10 })
 */
export function IDBGetAll<T>(storeName: string, options?: IDBQueryOptions): Promise<T[]> {
  return new Promise((resolve, reject) => {
    if (!currentDB) {
      reject(new Error('Database not opened. Call IDBOpen first.'))
      return
    }
    
    const transaction = currentDB.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    
    let request: IDBRequest<T[]>
    
    if (options?.indexName) {
      const index = store.index(options.indexName)
      request = index.getAll(options.range, options.limit)
    } else {
      request = store.getAll(options?.range, options?.limit)
    }
    
    request.onsuccess = () => {
      resolve(request.result)
    }
    
    request.onerror = () => {
      reject(new Error(`Failed to get all items: ${request.error?.message}`))
    }
  })
}
/**
 * @doc true
 * @description 清空表中所有数据（保留表结构）
 * @param {string} storeName - 表名
 * @returns {Promise<void>}
 * @example
 * // 清空 users 表
 * await PRL.IDBClear('users')
 * 
 * // 重置所有数据
 * await PRL.IDBClear('users')
 * await PRL.IDBClear('products')
 */
export function IDBClear(storeName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!currentDB) {
      reject(new Error('Database not opened. Call IDBOpen first.'))
      return
    }
    
    const transaction = currentDB.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.clear()
    
    request.onsuccess = () => {
      resolve()
    }
    
    request.onerror = () => {
      reject(new Error(`Failed to clear store: ${request.error?.message}`))
    }
  })
}
/**
 * @doc true
 * @description 通过主键删除一条数据
 * @param {string} storeName - 表名
 * @param {IDBValidKey} key - 主键值
 * @returns {Promise<void>}
 * @example
 * // 删除 id 为 1 的用户
 * await PRL.IDBDelete('users', 1)
 */
export function IDBDelete(storeName: string, key: IDBValidKey): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!currentDB) {
      reject(new Error('Database not opened. Call IDBOpen first.'))
      return
    }
    
    const transaction = currentDB.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.delete(key)
    
    request.onsuccess = () => {
      resolve()
    }
    
    request.onerror = () => {
      reject(new Error(`Failed to delete item: ${request.error?.message}`))
    }
  })
}

export function IDBGet<T>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    if (!currentDB) {
      reject(new Error('Database not opened. Call IDBOpen first.'))
      return
    }
    
    const transaction = currentDB.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.get(key)
    
    request.onsuccess = () => {
      resolve(request.result)
    }
    
    request.onerror = () => {
      reject(new Error(`Failed to get item: ${request.error?.message}`))
    }
  })
}
export function IDBUpdate<T>(storeName: string, item: T): Promise<IDBValidKey> {
  return new Promise((resolve, reject) => {
    if (!currentDB) {
      reject(new Error('Database not opened. Call IDBOpen first.'))
      return
    }
    
    const transaction = currentDB.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.put(item)
    
    request.onsuccess = () => {
      resolve(request.result)
    }
    
    request.onerror = () => {
      reject(new Error(`Failed to update item: ${request.error?.message}`))
    }
  })
}











/**
 * @doc true
 * @description 删除整个数据库
 * @param {string} dbName - 数据库名称
 * @returns {Promise<void>}
 * @example
 * // 删除数据库
 * await PRL.IDBDeleteDB('myApp')
 */
export function IDBDeleteDB(dbName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (currentDB && currentDBName === dbName) {
      currentDB.close()
      currentDB = null
      currentDBName = ''
    }
    
    const request = indexedDB.deleteDatabase(dbName)
    
    request.onsuccess = () => {
      resolve()
    }
    
    request.onerror = () => {
      reject(new Error(`Failed to delete database: ${request.error?.message}`))
    }
  })
}

/**
 * @doc true
 * @description 关闭当前打开的数据库
 * @returns {void}
 * @example
 * // 关闭数据库
 * PRL.IDBClose()
 */
export function IDBClose(): void {
  if (currentDB) {
    currentDB.close()
    currentDB = null
    currentDBName = ''
  }
}

/**
 * @doc true
 * @description 获取当前环境中所有数据库名称
 * @returns {Promise<string[]>} 数据库名称数组
 * @example
 * // 获取所有数据库名
 * const dbNames = await PRL.IDBGetNames()
 * console.log(dbNames)
 */
export function IDBGetNames(): Promise<string[]> {
  return new Promise((resolve, reject) => {
    if ('databases' in indexedDB) {
      (indexedDB as any).databases().then((databases: { name: string }[]) => {
        resolve(databases.map((db) => db.name))
      }).catch((error: Error) => {
        reject(new Error(`Failed to get database names: ${error.message}`))
      })
    } else {
      reject(new Error('IDBGetNames is not supported in this browser'))
    }
  })
}

/**
 * @doc true
 * @description 检查当前环境是否支持 IndexedDB
 * @returns {boolean} 是否支持
 * @example
 * // 检查是否支持
 * if (PRL.IDBIsAvailable()) {
 *   console.log('IndexedDB 可用')
 * } else {
 *   console.log('IndexedDB 不可用')
 * }
 */
export function IDBIsAvailable(): boolean {
  try {
    return 'indexedDB' in window && window.indexedDB !== null
  } catch {
    return false
  }
}

/**
 * @doc true
 * @description 在现有数据库中创建新表（自动升级版本）
 * @param {string} dbName - 数据库名称
 * @param {string} storeName - 表名
 * @param {IDBObjectStoreOptions} [options] - 表配置选项
 * @param {string} [options.keyPath='id'] - 主键字段
 * @param {boolean} [options.autoIncrement=true] - 是否自增
 * @param {IDBIndexConfig[]} [options.indices] - 索引配置
 * @returns {Promise<IDBDatabase>} IndexedDB 数据库实例
 * @example
 * // 在现有数据库中创建新表
 * const db = await PRL.IDBCreateStoreWithIndices('myApp', 'products', {
 *   keyPath: 'id',
 *   autoIncrement: true,
 *   indices: [{ fieldName: 'name' }]
 * })
 */
export function IDBCreateStoreWithIndices(
  dbName: string,
  storeName: string,
  options: IDBObjectStoreOptions = {}
): Promise<IDBDatabase> {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await IDBOpen({ dbName, version: 1 })
      
      if (!db.objectStoreNames.contains(storeName)) {
        db.close()
        currentDB = null
        currentDBName = ''
        
        const newVersion = db.version + 1
        const newDB = await IDBOpen({ dbName, version: newVersion })
        
        const { keyPath = 'id', autoIncrement = true, indices = [] } = options
        const store = newDB.createObjectStore(storeName, { keyPath, autoIncrement })
        
        indices.forEach((index) => {
          const indexKeyPath = index.keyPath || index.fieldName
          store.createIndex(index.fieldName, indexKeyPath, index.options || {})
        })
        
        resolve(newDB)
      } else {
        resolve(db)
      }
    } catch (error: any) {
      reject(new Error(`Failed to create store with indices: ${error.message}`))
    }
  })
}
